import { inject, makeStateKey, PLATFORM_ID, TransferState } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ResolveFn } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { ProductsService } from '../../../product/products.service';
import { SignupCategory } from '../../../product/product.types';
import { ApiService } from '../../../shared/api.service';
import { RequestHandlerParams } from '../../../shared/types/api-service.types';

/**
 * Resolver data structure for farmbox products and frequencies
 */
export interface FarmboxStepData {
    categories: SignupCategory[];
    frequencies: any[];
    error: string | null;
}

const FARMBOX_STATE_KEY = makeStateKey<FarmboxStepData>('signup-farmbox-step');

/**
 * Resolver that returns farmbox products grouped by category, plus frequencies.
 *
 * Hands the server's result to the client through TransferState.
 *
 * Client hydration is disabled (see app.config.ts), so on a reload Angular
 * discards the server's DOM and renders the page again from scratch. The Router
 * will not paint the route until this resolver completes — and without the
 * transfer it re-requested /categories/sign-up and /subscriptions/frequencies
 * over the network, which is what the blank window between the two renders was
 * made of. Reading the payload out of the HTML instead makes the resolver settle
 * synchronously, so the second render lands immediately after the first is torn
 * down rather than ~800 ms later.
 *
 * `withHttpTransferCache` would have covered this automatically, but it ships
 * inside provideClientHydration. TransferState is independent of hydration —
 * menu.service already relies on it for exactly this reason.
 */
export const farmboxStepResolver: ResolveFn<FarmboxStepData> = () => {
    const productsService = inject(ProductsService);
    const apiService = inject(ApiService);
    const transferState = inject(TransferState);
    const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

    // On the client, take what the server already serialized and skip the calls.
    // Removed after reading so a later in-app navigation fetches fresh data
    // instead of replaying a payload from page load.
    if (isBrowser && transferState.hasKey(FARMBOX_STATE_KEY)) {
        const cached = transferState.get(FARMBOX_STATE_KEY, null as unknown as FarmboxStepData);
        transferState.remove(FARMBOX_STATE_KEY);
        if (cached) return of(cached);
    }

    const frequenciesParams: RequestHandlerParams = {
        method: 'GET',
        endpoint: '/subscriptions/frequencies',
        apiV3: true,
    };

    const frequencies$ = apiService.handleRequest<any[]>(frequenciesParams).pipe(
        map(response => response || []),
        catchError(err => {
            console.error('Error loading frequencies in resolver:', err);
            return of([] as any[]);
        })
    );

    const categories$ = productsService
        .getSignupCategories()
        .pipe(
            map(response => response.data || []),
            catchError(err => {
                console.error('Error loading products in resolver:', err);
                return of([]);
            })
        );

    return forkJoin({
        categories: categories$,
        frequencies: frequencies$
    }).pipe(
        map(({ categories, frequencies }) => {
            return {
                categories,
                frequencies,
                error: null
            };
        }),
        // Only the server writes the payload; the client consumed it above.
        // Failures are not stored, so a bad server render does not hand the
        // client an empty result it would otherwise have fetched itself.
        tap(data => {
            if (!isBrowser) transferState.set(FARMBOX_STATE_KEY, data);
        }),
        catchError(err => {
            console.error('Error in farmbox products resolver:', err);
            return of({
                categories: [],
                frequencies: [],
                error: 'Failed to load farmboxes. Please try again.'
            });
        })
    );
};
